/* eslint-disable no-console */
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Cart, RecoveredCart, RecoveredCartItem, AddItemProps } from '@pegasus/cart';
import { useConfig } from '@browsing/config';

import {
    showHeaderSuccessFlashMessage,
    showHeaderErrorFlashMessage,
    showHeaderWarningFlashMessage,
} from '@client/redux/modules/flashMessage/actions';
import { track, getTrackingDataRemoveFromCart } from '@client/helpers/tracking';

import { setCart, resetCart, setRecoveredCart } from './redux/actions';
import { getCart, getRecoveredCart } from './redux/selectors';
import * as cartServices from './services';
import { transformCartItems, manageAddToCartError } from './helpers';

interface CartActions {
    onItemRemove: (sku: string) => Promise<void>;
    onAllItemsRemove: () => Promise<void>;
    onItemReserveAgain: (props: AddItemProps & { successFlashMessage?: string }) => Promise<void>;
    onExtendCart: () => Promise<void>;
    onExpiredCart: () => void;
}

interface CompositeCart {
    cart: Cart;
    recoveredCart: RecoveredCart;
}

// we use a global variable to trigger
// the fetch of the cart on mount only once
let shouldFetch = true;

/**
 * Cart functions hook.
 * This hook is good as long as we don't need
 * to share the state among different components
 * when needed, extract the state logic into context API or redux
 */
export const useCart = (): [CompositeCart, CartActions] => {
    const config = useConfig();
    const cart = useSelector(getCart);
    const recoveredCart = useSelector(getRecoveredCart);
    const dispatch = useDispatch();

    const trackRemoveFromCart = useCallback(
        (simpleSkuRemoved: string) => {
            try {
                const cartItem = cart.items.find(({ simpleSku }) => simpleSku === simpleSkuRemoved);
                const trackingDataRemoveFromCart = getTrackingDataRemoveFromCart(cartItem);
                track('removeFromCart', trackingDataRemoveFromCart);
            } catch (e) {
                console.error(e);
            }
        },
        [cart],
    );

    const fetchCart = useCallback(
        () =>
            cartServices
                .getCart()
                .then(currCart => {
                    if (currCart) {
                        dispatch(
                            setCart({
                                ...currCart,
                                items: transformCartItems(currCart.items, config),
                            }),
                        );
                    }
                })
                .catch(console.error),
        [config, dispatch],
    );

    const fetchRecoveredCart = useCallback(async () => {
        try {
            // TODO: Temporarily disable the api call for the incomplete api, till it's fixed
            // https://jira.zalando.net/browse/ZLPC-2760
            // const recoveredCart = await cartServices.getRecoveredCart();
            const updatedRecoveredCart: RecoveredCart = { items: [] };

            dispatch(
                setRecoveredCart({
                    items: transformCartItems<RecoveredCartItem>(
                        updatedRecoveredCart?.items,
                        config,
                    ),
                }),
            );
        } catch (error) {
            console.error(error);
        }
    }, [config, dispatch]);

    const onItemRemove = useCallback(
        (itemSimpleSku: string) =>
            cartServices
                .deleteCartItem(itemSimpleSku)
                .then(() => {
                    trackRemoveFromCart(itemSimpleSku);

                    return fetchCart();
                })
                .catch(console.error),
        [fetchCart, trackRemoveFromCart],
    );

    const onAllItemsRemove = useCallback(
        () =>
            cartServices
                .deleteAllItemsFromCart()
                .then(() => {
                    return fetchCart();
                })
                .catch(console.error),
        [fetchCart],
    );

    const addItemToCart = useCallback((addToCartProps: AddItemProps) => {
        return cartServices
            .addItemToCart({
                ...addToCartProps,
            })
            .then(({ error }) => {
                if (error) {
                    return Promise.reject(manageAddToCartError(error));
                }

                return Promise.resolve();
            });
    }, []);

    const onItemReserveAgain: CartActions['onItemReserveAgain'] = useCallback(
        async ({ successFlashMessage = '', ...item }) => {
            try {
                await addItemToCart(item);
                await fetchCart();
                if (successFlashMessage) {
                    dispatch(showHeaderSuccessFlashMessage(successFlashMessage));
                }
            } catch (error) {
                if (error.errorMessage) {
                    dispatch(showHeaderErrorFlashMessage(error.errorMessage));
                } else if (error.warningMessage) {
                    dispatch(showHeaderWarningFlashMessage(error.warningMessage));
                }
            }
        },
        [addItemToCart, dispatch, fetchCart],
    );

    const onExtendCart = useCallback(
        () => cartServices.extendCart().then(fetchCart).catch(console.error),
        [fetchCart],
    );

    const onExpiredCart = useCallback(() => {
        dispatch(resetCart());
    }, [dispatch]);

    useEffect(() => {
        if (shouldFetch) {
            shouldFetch = false;
            fetchCart();
        }
    }, [fetchCart]);

    useEffect(() => {
        fetchRecoveredCart();
    }, [fetchRecoveredCart]);

    return [
        { cart, recoveredCart },
        {
            onItemRemove,
            onAllItemsRemove,
            onItemReserveAgain,
            onExtendCart,
            onExpiredCart,
        },
    ];
};
